import {
  MerchantApplicationAIPrefillingResponse,
  MerchantApplicationResponse,
} from 'types/responses/activation';

export const ACTIVATION_DATA_LOCAL_STORAGE_KEY = 'merchantApplicationData';

const MONEY_RANGE_LABEL_LOOKUP = new Map([
  ['$0-$100', 'Range0To100'],
  ['$100-$500', 'Range100To500'],
  ['$500-$1000', 'Range500To1000'],
  ['$1000-$3000', 'Range1000To3000'],
  ['$3000+', 'Range3000Plus'],
  ['$500-$1,000', 'Range500To1000'],
  ['$1,000-$3,000', 'Range1000To3000'],
  ['$3,000+', 'Range3000Plus'],
]);

const normaliseMoneyRangeValue = (
  rawValue: string | null | undefined
): string | null | undefined => {
  if (!rawValue) {
    return rawValue;
  }

  if (
    rawValue === 'Range0To100' ||
    rawValue === 'Range100To500' ||
    rawValue === 'Range500To1000' ||
    rawValue === 'Range1000To3000' ||
    rawValue === 'Range3000Plus'
  ) {
    return rawValue;
  }

  return MONEY_RANGE_LABEL_LOOKUP.get(rawValue.replace(/\s+/g, '')) ?? rawValue;
};

// Maps raw AI shipping time text to the ShippingTimeFrameInDays enum value
// expected by the API. The AI may return labels with different dash/spacing variants.
const AI_SHIPPING_TIME_MAP: Record<string, string> = {
  '0 - 7 days': 'Range0To7',
  '8 - 14 days': 'Range7To14',
  '15 - 29 days': 'Range15To30',
  '0-7 days': 'Range0To7',
  '8-14 days': 'Range7To14',
  '15-29 days': 'Range15To30',
  '0–7 days': 'Range0To7',
  '8–14 days': 'Range7To14',
  '15–29 days': 'Range15To30',
  '30+ days': 'Range30Plus',
};

const SHIPPING_TIME_FRAME_VALUES = new Set([
  'Range0To7',
  'Range7To14',
  'Range15To30',
  'Range30Plus',
]);

export const mapAIShippingTimeFrame = (
  aiValue: string | null | undefined
): string | undefined => {
  if (!aiValue) return undefined;

  if (SHIPPING_TIME_FRAME_VALUES.has(aiValue)) {
    return aiValue;
  }

  return AI_SHIPPING_TIME_MAP[aiValue] ?? undefined;
};

/**
 * The .NET API returns aiPrefilling fields in PascalCase.
 * Normalise to camelCase to match our TypeScript types.
 * Applied both on fresh API responses and when reading from localStorage
 * so stale PascalCase data is handled transparently.
 */
export const normaliseAIPrefilling = (
  raw: Record<string, unknown> | null | undefined
): MerchantApplicationAIPrefillingResponse | null => {
  if (!raw) return null;

  const rawInfo = (raw['BusinessInfo'] ?? raw['businessInfo']) as
    | Record<string, unknown>
    | null
    | undefined;

  if (!rawInfo) return null;

  const str = (key: string): string | null => {
    const v = rawInfo[key];
    return typeof v === 'string' ? v : null;
  };

  const bool = (key: string): boolean | null => {
    const v = rawInfo[key];
    return typeof v === 'boolean' ? v : null;
  };

  const moneyRange = (key: string, camelKey: string): string | null => {
    const value = str(key) ?? str(camelKey);
    return normaliseMoneyRangeValue(value) ?? null;
  };

  const shippingTime = (key: string, camelKey: string): string | null => {
    const value = str(key) ?? str(camelKey);
    return mapAIShippingTimeFrame(value) ?? value;
  };

  return {
    businessInfo: {
      url: str('Url') ?? str('url'),
      applicationId: str('ApplicationId') ?? str('applicationId'),
      franchiseGroup: bool('FranchiseGroup') ?? bool('franchiseGroup'),
      franchiseGroupConfidence:
        str('FranchiseGroupConfidence') ?? str('franchiseGroupConfidence'),
      multipleLocations: bool('MultipleLocations') ?? bool('multipleLocations'),
      multipleLocationsConfidence:
        str('MultipleLocationsConfidence') ??
        str('multipleLocationsConfidence'),
      avgTransactionValue: moneyRange(
        'AvgTransactionValue',
        'avgTransactionValue'
      ),
      avgTransactionValueConfidence:
        str('AvgTransactionValueConfidence') ??
        str('avgTransactionValueConfidence'),
      costOfLargestProduct: moneyRange(
        'CostOfLargestProduct',
        'costOfLargestProduct'
      ),
      costOfLargestProductConfidence:
        str('CostOfLargestProductConfidence') ??
        str('costOfLargestProductConfidence'),
      avgShipTime: shippingTime('AvgShipTime', 'avgShipTime'),
      avgShipTimeConfidence:
        str('AvgShipTimeConfidence') ?? str('avgShipTimeConfidence'),
      shippingPolicy: bool('ShippingPolicy') ?? bool('shippingPolicy'),
      shippingPolicyConfidence:
        str('ShippingPolicyConfidence') ?? str('shippingPolicyConfidence'),
      returnPolicy: bool('ReturnPolicy') ?? bool('returnPolicy'),
      returnPolicyConfidence:
        str('ReturnPolicyConfidence') ?? str('returnPolicyConfidence'),
      businessLocation: str('BusinessLocation') ?? str('businessLocation'),
      businessLocationConfidence:
        str('BusinessLocationConfidence') ?? str('businessLocationConfidence'),
    },
  };
};

export type StoredActivationData = MerchantApplicationResponse & {
  lastUpdated?: number;
  businessDetailsStepAAIConfirmed?: boolean;
  businessDetailsStepBAIConfirmed?: boolean;
  businessDetailsStepCAIConfirmed?: boolean;
};

export const getStoredActivationData = (): StoredActivationData | null => {
  try {
    const stored = localStorage.getItem(ACTIVATION_DATA_LOCAL_STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as StoredActivationData & {
      aiPrefilling?: Record<string, unknown> | null;
    };

    // Normalise aiPrefilling on every read so stale PascalCase data from
    // earlier sessions is handled transparently.
    return {
      ...parsed,
      aiPrefilling: normaliseAIPrefilling(
        parsed.aiPrefilling as Record<string, unknown>
      ),
    };
  } catch {
    return null;
  }
};

export const saveActivationDataToLocalStorage = (
  data: StoredActivationData | MerchantApplicationResponse
): void => {
  try {
    const storageData: StoredActivationData = {
      ...data,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(
      ACTIVATION_DATA_LOCAL_STORAGE_KEY,
      JSON.stringify(storageData)
    );
  } catch (err) {
    console.error('Failed to save application to localStorage:', err);
  }
};
